<template>
    <div class="preproduction-panel" v-if="loaded">

        <!-- Gate state strip -->
        <div class="pp-strip" :class="gate.ok ? 'pp-strip-ok' : 'pp-strip-blocked'">
            <div class="pp-strip-main">
                <i class="fa" :class="gate.ok ? 'fa-check-circle' : 'fa-hourglass-half'"></i>
                <strong v-if="gate.ok">Pre-production complete — ready to release</strong>
                <strong v-else>Pre-production in progress</strong>
                <span v-if="express" class="pp-express" title="Under the express thresholds — auto-plan confirmed itself; the three named actions still apply">⚡ EXPRESS</span>
                <ul v-if="!gate.ok" class="pp-strip-list">
                    <li v-for="(msg, i) in gate.blocking" :key="'b' + i"><i class="fa fa-lock"></i> {{ msg }}</li>
                </ul>
                <ul v-if="gate.warnings.length" class="pp-strip-list pp-warn">
                    <li v-for="(msg, i) in gate.warnings" :key="'w' + i"><i class="fa fa-exclamation-triangle"></i> {{ msg }}</li>
                </ul>
            </div>
            <!-- The same production sheet the floor works from — the scribble
                 canvas: view dims, scribble measurements, stamp Measured-by. -->
            <div class="pp-strip-actions" v-if="items.length">
                <div class="pp-sheet-lead">
                    <i class="fa fa-file-text-o"></i>
                    Production sheet<template v-if="items.length > 1"> — one per item</template>
                </div>
                <a
                    v-for="(it, i) in visibleItems"
                    :key="'sheet' + it.id"
                    class="btn btn-default pp-sheet-btn"
                    :href="sheetUrl(it.id)"
                    data-ajax="false"
                    target="_blank"
                    rel="noopener"
                    :title="'Open the production sheet — ' + itemLabelFull(it) + ' — measure, scribble, stamp Measured-by'"
                >{{ itemLabel(it, i, items.length) }}</a>
                <button
                    v-if="items.length > sheetLimit"
                    type="button"
                    class="btn btn-link btn-sm pp-sheet-more"
                    @click="showAllSheets = !showAllSheets"
                >{{ showAllSheets ? 'Show fewer' : ('+ ' + (items.length - sheetLimit) + ' more items') }}</button>
            </div>
        </div>

        <div class="pp-tasks">

            <!-- Task 1: Dimensions & spec check (two people, anyone in the team) -->
            <div class="pp-card" :class="{ 'pp-done': isDone('dimensions_check') }">
                <div class="pp-card-head">
                    <i class="fa fa-arrows-alt"></i> Dimensions &amp; spec
                    <span class="pp-owner">2 people</span>
                </div>
                <div class="pp-card-body">
                    <div class="pp-check-row">
                        <button
                            v-if="!task('dimensions_check').completed_by"
                            class="btn btn-default pp-big-btn"
                            :disabled="busy"
                            @click="dimensionsCheck"
                        ><i class="fa fa-check"></i> Checked it</button>
                        <span v-else class="pp-name-stamp">
                            <i class="fa fa-check-circle text-success"></i>
                            {{ task('dimensions_check').completed_by.name }}
                        </span>
                    </div>
                    <div class="pp-check-row">
                        <button
                            v-if="task('dimensions_check').completed_by && !task('dimensions_check').completed_at"
                            class="btn btn-default pp-big-btn"
                            :disabled="busy"
                            @click="dimensionsCheck"
                        ><i class="fa fa-check"></i> Second check</button>
                        <span v-else-if="task('dimensions_check').completed_at && task('dimensions_check').second_check_by" class="pp-name-stamp">
                            <i class="fa fa-check-circle text-success"></i>
                            Second check ✓
                        </span>
                        <span v-else-if="isPreCompleted('dimensions_check')" class="pp-name-stamp text-muted">
                            Pre-completed — redo
                        </span>
                        <span v-else class="text-muted pp-hint">needs a different person</span>
                    </div>
                    <small v-if="task('dimensions_check').notes" class="text-muted pp-stale-note">
                        {{ lastNoteLine(task('dimensions_check').notes) }}
                    </small>
                </div>
            </div>

            <!-- Task 2: Materials (Azfar's) -->
            <div class="pp-card" :class="{ 'pp-done': isDone('materials') }">
                <div class="pp-card-head">
                    <i class="fa fa-cubes"></i> Materials
                    <span class="pp-owner" v-if="task('materials').owner">{{ task('materials').owner.name }}'s</span>
                </div>
                <div class="pp-card-body">
                    <div class="pp-check-row" v-for="box in ['stock_checked', 'ordered']" :key="box">
                        <label class="pp-checkbox">
                            <input
                                type="checkbox"
                                :checked="!!materialsBox(box)"
                                :disabled="busy"
                                @change="tickMaterials(box, $event)"
                            />
                            <span>{{ box === 'stock_checked' ? 'Stock checked' : 'Ordered / in stock' }}</span>
                        </label>
                        <small v-if="materialsBox(box)" class="text-muted">
                            {{ materialsBox(box).user_name }}
                        </small>
                    </div>
                    <small v-if="isPreCompleted('materials')" class="text-muted">Pre-completed — redo</small>
                </div>
            </div>

            <!-- Task 3: Completion dates (Kev's) -->
            <div class="pp-card" :class="{ 'pp-done': isDone('completion_date') }">
                <div class="pp-card-head">
                    <i class="fa fa-calendar-check-o"></i> Completion dates
                    <span class="pp-owner" v-if="task('completion_date').owner">{{ task('completion_date').owner.name }}'s</span>
                </div>
                <div class="pp-card-body">
                    <div class="pp-date-line">
                        <span class="pp-date-label">Planned finish</span>
                        <strong>{{ plannedCompletion || '— not scheduled yet' }}</strong>
                    </div>
                    <div class="pp-date-line" v-if="deadlineText">
                        <span class="pp-date-label">Client wants</span>
                        <span>{{ deadlineText }}</span>
                    </div>
                    <div class="pp-check-row">
                        <button
                            v-if="!isDone('completion_date')"
                            class="btn btn-default pp-big-btn"
                            :disabled="busy || !plannedCompletion"
                            :title="plannedCompletion ? 'Agree this date — you will commit it to the client' : 'Schedule the job first'"
                            @click="agreeDates"
                        ><i class="fa fa-handshake-o"></i> Agree date</button>
                        <span v-else class="pp-name-stamp">
                            <i class="fa fa-check-circle text-success"></i>
                            {{ agreedDate }} agreed
                        </span>
                    </div>
                    <small v-if="task('completion_date').notes" class="text-warning pp-stale-note">
                        {{ lastNoteLine(task('completion_date').notes) }}
                    </small>
                </div>
            </div>

            <!-- Task 4: Schedule to people (Pete M's) -->
            <div class="pp-card pp-card-wide" :class="{ 'pp-done': isDone('schedule') }">
                <div class="pp-card-head">
                    <i class="fa fa-users"></i> Schedule to people
                    <span class="pp-owner" v-if="task('schedule').owner">{{ task('schedule').owner.name }}'s</span>
                </div>
                <div class="pp-card-body">
                    <div v-if="!processes.length" class="pp-check-row">
                        <button class="btn btn-primary pp-big-btn" :disabled="busy" @click="planJob">
                            <i class="fa fa-magic"></i> Plan this job
                        </button>
                        <span class="pp-hint text-muted">splits into processes and drafts a plan</span>
                    </div>

                    <template v-else>
                        <!-- P3: editing moved to the planner's assign mode — this card is a summary
                             (walkthrough decision 2026-07-13, design §4.2). Rename stays: it's a
                             pre-production clarity job, not a scheduling one. -->
                        <div class="pp-sched-summary">
                            <strong>{{ openProcesses.length - unslottedCount }} of {{ openProcesses.length }}</strong> processes assigned
                            <span v-if="plannedCompletion"> · planned completion <strong>{{ plannedCompletion }}</strong></span>
                        </div>

                        <div
                            v-for="proc in openProcesses"
                            :key="proc.id"
                            class="pp-process"
                            :class="{ 'pp-process-unslotted': !proc.slots.length }"
                        >
                            <span class="pp-process-name">
                                {{ proc.pretty_name }}
                                <button
                                    class="btn btn-link btn-xs pp-rename"
                                    :disabled="busy"
                                    title="Rename — make it unambiguous for the floor"
                                    @click="renameProcess(proc)"
                                ><i class="fa fa-pencil"></i></button>
                            </span>
                            <span class="pp-process-time">{{ proc.est_minutes }}m</span>
                            <span class="pp-slots-ro">
                                <template v-if="proc.slots.length">
                                    <span v-for="slot in proc.slots" :key="slot.id" class="pp-slot-ro" :class="{ 'pp-slot-draft': slot.is_draft }">
                                        {{ slot.user ? slot.user.name : '?' }} · {{ shortDate(slot.date) }}
                                        <span v-if="slot.is_draft" class="pp-draft-badge" title="auto-drafted — Confirm plan makes it real">draft</span>
                                    </span>
                                </template>
                                <span v-else class="pp-unslotted-tag">needs a person + day</span>
                            </span>
                        </div>

                        <div class="pp-addproc" v-if="addProc.open">
                            <input v-model="addProc.name" class="form-control input-sm" placeholder="Process name (as the floor should read it)" style="max-width:260px">
                            <input v-model.number="addProc.minutes" type="number" min="1" max="10000" class="form-control input-sm" placeholder="mins" style="width:80px">
                            <select v-model="addProc.workcentreId" class="form-control input-sm" style="max-width:160px">
                                <option :value="null">Workcentre…</option>
                                <option v-for="wc in workcentres" :key="wc.id" :value="wc.id">{{ wc.name }}</option>
                            </select>
                            <select v-if="items.length > 1" v-model="addProc.itemId" class="form-control input-sm" style="max-width:220px">
                                <option v-for="(it, i) in items" :key="it.id" :value="it.id">{{ itemLabel(it, i, items.length) }}</option>
                            </select>
                            <button class="btn btn-primary btn-sm" :disabled="busy || !addProc.name || !addProc.minutes" @click="submitAddProcess">Add</button>
                            <button class="btn btn-link btn-sm" @click="addProc.open = false">cancel</button>
                        </div>
                        <div class="pp-check-row" v-else>
                            <button class="btn btn-link btn-sm pp-addproc-toggle" :disabled="busy" @click="openAddProcess">
                                <i class="fa fa-plus"></i> Add a process
                            </button>
                        </div>

                        <div class="pp-check-row pp-confirm-row">
                            <a class="btn btn-default pp-big-btn" :href="plannerUrl" data-ajax="false">
                                <i class="fa fa-calendar"></i> Assign on the planner →
                            </a>
                            <button
                                v-if="!isDone('schedule')"
                                class="btn btn-primary pp-big-btn"
                                :disabled="busy || unslottedCount > 0"
                                :title="unslottedCount > 0 ? unslottedCount + ' processes still need a person and a day' : 'Confirm the reviewed plan'"
                                @click="confirmPlan"
                            ><i class="fa fa-check"></i> Confirm plan</button>
                            <span v-else class="pp-name-stamp">
                                <i class="fa fa-check-circle text-success"></i>
                                Plan confirmed
                            </span>
                            <span v-if="unslottedCount > 0" class="pp-hint text-muted">
                                {{ unslottedCount }} still unassigned
                            </span>
                        </div>
                    </template>
                </div>
            </div>

        </div>

        <!-- Notes thread: append-only, anyone, lock-immune -->
        <div class="pp-card pp-notes">
            <div class="pp-card-head"><i class="fa fa-sticky-note"></i> Notes</div>
            <div class="pp-card-body">
                <textarea v-model="noteBody" class="form-control" rows="2" maxlength="2000"
                          placeholder="Add a note on this quote…"></textarea>
                <button class="btn btn-default pp-note-add" :disabled="busy || !noteBody.trim()" @click="addNote">
                    <i class="fa fa-plus"></i> Add note
                </button>
                <div v-for="n in notes" :key="'note' + n.id" class="pp-note-row">
                    <div class="pp-note-meta"><strong>{{ n.user_name }}</strong> · {{ n.created_at }}</div>
                    <div class="pp-note-body">{{ n.body }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/*
    Gate 2 pre-production workspace panel — the per-quote task list on
    the Won tab. Each task has a purpose-built display (design §4.2):
    the two-person dimensions check, Azfar's stamped materials
    checkboxes, Kev's completion-date agreement, and Pete M's schedule
    task (Plan this job → per-process soonest-free-spot → Confirm plan).

    Owner rules are enforced server-side; a 403/422 surfaces the reason.
    Override users get a stand-in prompt on refusal. Dyslexia-first:
    icon-led, big targets, minimal text.
*/
import { productionSheetUrl, itemLabel, itemLabelFull } from '../planner/shared';

// A mega-order can carry 40+ items; show a readable handful and let the
// rest open on demand rather than burying the task cards.
const SHEET_LIMIT = 8;

export default {
    props: {
        quoteId: { type: Number, required: true },
        canOverride: { type: Boolean, default: false },
    },
    data() {
        return {
            loaded: false,
            busy: false,
            tasks: [],
            processes: [],
            items: [],
            people: [],
            gate: { ok: false, blocking: [], warnings: [] },
            plannedCompletion: null,
            deadlineText: '',
            express: false,
            workcentres: [],
            addProc: { open: false, name: '', minutes: null, workcentreId: null, itemId: null },
            notes: [],
            noteBody: '',
            showAllSheets: false,
            sheetLimit: SHEET_LIMIT,
        };
    },
    computed: {
        visibleItems() {
            return this.showAllSheets ? this.items : this.items.slice(0, SHEET_LIMIT);
        },
        plannerUrl() {
            return '/planner?quote=' + this.quoteId + '&assign=1';
        },
        openProcesses() {
            return this.processes.filter(p => !p.checked);
        },
        itemIds() {
            return [...new Set(this.processes.map(p => p.item_id).filter(Boolean))];
        },
        unslottedCount() {
            return this.openProcesses.filter(p => !p.slots.length).length;
        },
        agreedDate() {
            const t = this.task('completion_date');
            return (t.checklist && t.checklist.agreed_date) ? this.shortDate(t.checklist.agreed_date) : '';
        },
    },
    mounted() { this.refresh(); },
    methods: {
        refresh() {
            fetch(`/quote/${this.quoteId}/preproduction`, { headers: { Accept: 'application/json' }})
                .then(r => r.json())
                .then(d => {
                    this.tasks = d.tasks || [];
                    this.processes = d.processes || [];
                    this.items = d.items || [];
                    this.people = d.people || [];
                    this.gate = d.gate || this.gate;
                    this.plannedCompletion = d.planned_completion;
                    this.express = !!d.express;
                    this.workcentres = d.workcentres || [];
                    this.deadlineText = d.deadline_text || '';
                    this.loaded = true;
                })
                .catch(() => { this.loaded = false; });

            window.axios.get(`/quote/${this.quoteId}/notes`)
                .then(res => { this.notes = res.data.notes || []; })
                .catch(() => { this.notes = []; });
        },
        addNote() {
            if (!this.noteBody.trim()) return;
            this.busy = true;
            window.axios.post(`/quote/${this.quoteId}/notes`, { body: this.noteBody.trim() })
                .then(res => {
                    this.notes.unshift(res.data.note);
                    this.noteBody = '';
                })
                .catch(() => {})
                .then(() => { this.busy = false; });
        },
        sheetUrl(itemId) {
            return productionSheetUrl(itemId);
        },
        itemLabel,
        itemLabelFull,
        task(type) {
            return this.tasks.find(t => t.task === type) || { checklist: {}, owner: null };
        },
        isDone(type) {
            return !!this.task(type).completed_at;
        },
        isPreCompleted(type) {
            const t = this.task(type);
            return !!(t.checklist && t.checklist.pre_completed);
        },
        materialsBox(box) {
            const t = this.task('materials');
            return t.checklist ? t.checklist[box] : null;
        },
        lastNoteLine(notes) {
            const lines = String(notes || '').trim().split('\n');
            return lines[lines.length - 1];
        },
        shortDate(iso) {
            if (!iso) return '';
            const d = new Date(iso + 'T00:00:00');
            return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        },

        post(url, body, method) {
            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            return fetch(url, {
                method: method || 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
                body: JSON.stringify(body || {}),
            }).then(r => r.json().then(b => ({ status: r.status, body: b })));
        },

        /**
         * Shared action runner: posts, and on an owner refusal offers the
         * override stand-in path (reason prompt) when the user can override.
         */
        act(url, body) {
            if (this.busy) return;
            this.busy = true;
            this.post(url, body)
                .then(({ status, body: resp }) => {
                    const needsStandIn = resp.needs_stand_in_reason === true
                        || (status === 403 && this.canOverride);
                    if (needsStandIn) {
                        const who = resp.owner || 'the owner';
                        const reason = window.prompt(
                            'This is ' + who + '’s action.\n\nStand in for them? Reason (min 10 chars):', ''
                        );
                        if (reason === null) return;
                        if (reason.trim().length < 10) {
                            alert('Not done — the stand-in reason must be at least 10 characters.');
                            return;
                        }
                        return this.post(url, Object.assign({}, body, { stand_in_reason: reason.trim() }))
                            .then(({ status: s2, body: b2 }) => {
                                if (s2 >= 400) alert(b2.message || b2.error || 'Refused');
                            });
                    }
                    if (status >= 400) {
                        const detail = resp.message || resp.error || resp.reason
                            || (resp.blocking && resp.blocking.join('\n'))
                            || (resp.unslotted && ('Still unassigned: ' + resp.unslotted.join(', ')))
                            || ('Refused (HTTP ' + status + ')');
                        alert(detail);
                    }
                })
                .catch(err => alert(err.message || 'Network error'))
                .then(() => { this.busy = false; this.refresh(); this.$emit('changed'); });
        },

        planJob() {
            this.act(`/quote/${this.quoteId}/preproduction/plan`, {});
        },
        dimensionsCheck() {
            this.act(`/quote/${this.quoteId}/preproduction/dimensions/check`, {});
        },
        tickMaterials(box, event) {
            this.act(`/quote/${this.quoteId}/preproduction/materials/tick`, {
                box,
                on: !!(event && event.target && event.target.checked),
            });
        },
        agreeDates() {
            this.act(`/quote/${this.quoteId}/preproduction/dates/agree`, {});
        },
        confirmPlan() {
            this.act(`/quote/${this.quoteId}/preproduction/confirm-plan`, {});
        },
        openAddProcess() {
            const firstItem = (this.items[0] && this.items[0].id) || this.itemIds[0] || null;
            this.addProc = { open: true, name: '', minutes: null, workcentreId: null, itemId: firstItem };
        },
        submitAddProcess() {
            // POST /checklist/process — the checklist page is retired, but its
            // endpoints live on; this is the add-custom-process new home (§4.8).
            this.act('/checklist/process', {
                item_id: this.addProc.itemId || this.itemIds[0],
                process_name: this.addProc.name.trim(),
                workcentre_id: this.addProc.workcentreId,
                minutes: this.addProc.minutes,
                num_people: 1,
            });
            this.addProc.open = false;
        },
        renameProcess(proc) {
            const name = window.prompt(
                'Rename this process so it reads unambiguously on the floor:',
                proc.process_name
            );
            if (name === null || !name.trim() || name.trim() === proc.process_name) return;
            this.act(`/quote/${this.quoteId}/preproduction/rename-process`, {
                production_checklist_id: proc.id,
                name: name.trim(),
            });
        },
    },
};
</script>

<style scoped>
.preproduction-panel { margin-bottom: 16px; }

.pp-express {
    display: inline-block; margin-left: 10px; background: #0b0b0b; color: #ffd977;
    font-size: 11px; font-weight: 800; letter-spacing: .05em;
    border-radius: 20px; padding: 2px 10px; vertical-align: middle;
}

.pp-strip {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 12px;
    font-size: 13px;
}
.pp-strip-main { flex: 1 1 260px; min-width: 0; }
.pp-strip-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    flex: 1 1 320px;
    max-width: 460px;
}
.pp-sheet-lead {
    flex-basis: 100%;
    text-align: right;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #6b7a89;
}
.pp-sheet-lead .fa { margin-right: 4px; }
/* Names are middle-ellipsised in JS, never by CSS — two items on one quote
   often differ only in their tail, and an end-cut label would make them
   look identical. */
.pp-sheet-btn {
    min-height: 34px;
    font-size: 12.5px;
    font-weight: 600;
    padding: 6px 11px;
}
.pp-sheet-more { padding: 0 4px; font-size: 12px; }
.pp-strip-ok { background: #e8f5ec; border: 1px solid #bfe3cb; }
.pp-strip-blocked { background: #fff7ec; border: 1px solid #f3ddb7; }
.pp-strip .fa { margin-right: 6px; }
.pp-strip-list { margin: 6px 0 0 22px; padding: 0; }
.pp-strip-list li { margin: 2px 0; list-style: none; }
.pp-strip-list .fa { width: 16px; }
.pp-warn li { color: #8a6d3b; }

.pp-tasks { display: flex; flex-wrap: wrap; gap: 12px; }
.pp-card {
    flex: 1 1 220px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    min-width: 220px;
}
.pp-card-wide { flex: 2 1 100%; }
.pp-done { border-color: #bfe3cb; background: #fbfffc; }
.pp-card-head {
    padding: 8px 12px;
    font-weight: 700;
    border-bottom: 1px solid #eef2f6;
    font-size: 13px;
}
.pp-card-head .fa { margin-right: 6px; color: #5b7b9a; }
.pp-owner {
    float: right;
    font-weight: 600;
    font-size: 11px;
    color: #fff;
    background: #5b7b9a;
    border-radius: 10px;
    padding: 1px 9px;
}
.pp-card-body { padding: 10px 12px; }

.pp-check-row { margin: 6px 0; display: flex; align-items: center; gap: 10px; }
.pp-big-btn { min-height: 38px; }
.pp-name-stamp { font-weight: 600; }
.pp-hint { font-size: 12px; }

.pp-checkbox { display: flex; align-items: center; gap: 8px; font-weight: 600; margin: 0; cursor: pointer; }
.pp-checkbox input { width: 20px; height: 20px; margin: 0; }

.pp-date-line { margin: 4px 0; }
.pp-date-label {
    display: inline-block;
    width: 100px;
    color: #718096;
    font-size: 12px;
}
.pp-stale-note { display: block; margin-top: 6px; }

.pp-process {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-bottom: 1px solid #f2f5f8;
    flex-wrap: wrap;
}
.pp-process-unslotted { background: #fffaf2; }

.pp-sched-summary { font-size: 14px; margin-bottom: 10px; color: #555; }
.pp-addproc { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; margin: 8px 0; }
.pp-addproc-toggle { padding-left: 0; }
.pp-slots-ro { display: flex; flex-wrap: wrap; gap: 5px; align-items: center; }
.pp-slot-ro {
    display: inline-flex; align-items: center; gap: 5px;
    background: #f5f4f1; border: 1px solid #e3e2dc; border-radius: 12px;
    padding: 2px 9px; font-size: 12px; font-weight: 600; white-space: nowrap;
}
.pp-slot-ro.pp-slot-draft { border-style: dashed; }
.pp-unslotted-tag { color: #a66f00; font-size: 12px; font-weight: 700; }
.pp-process-name { font-weight: 600; min-width: 140px; }
.pp-process-time { color: #718096; font-size: 12px; min-width: 44px; }
.pp-slots { display: flex; gap: 6px; flex-wrap: wrap; }
.pp-slot {
    background: #eef4fa;
    border: 1px solid #d4e2ef;
    border-radius: 10px;
    padding: 1px 10px;
    font-size: 12px;
}
.pp-slot-draft { background: repeating-linear-gradient(45deg, #f2f6fa, #f2f6fa 4px, #e8eef5 4px, #e8eef5 8px); }
.pp-slot-edit {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid #d4e2ef;
    border-radius: 8px;
    padding: 3px 6px;
    background: #eef4fa;
}
.pp-draft-badge {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #8a6d3b;
    background: #fdf3dd;
    border-radius: 8px;
    padding: 1px 7px;
}
.pp-rename { padding: 0 4px; color: #a0aec0; }
.pp-rename:hover { color: #2c4a73; }
.pp-slot-remove { padding: 0 4px; color: #a0aec0; }
.pp-slot-remove:hover { color: #c0392b; }
.pp-assign { display: inline-flex; gap: 6px; align-items: center; }
.pp-person-pick { width: 120px; display: inline-block; }
.pp-date-pick { width: 140px; display: inline-block; }
.pp-confirm-row { margin-top: 10px; padding-top: 10px; border-top: 1px solid #eef2f6; }
.pp-notes { margin-top: 14px; }
.pp-note-add { margin-top: 8px; }
.pp-note-row { border-top: 1px solid #eef2f6; padding: 8px 0; }
.pp-note-meta { font-size: 12px; color: #6b7a89; }
.pp-note-body { white-space: pre-wrap; margin-top: 2px; }
</style>
